import type { UnitOfMeasure } from './types/unit-of-measure'
import { VALIDATION_ERRORS } from './constants/validation-errors'
import {
  MAX_VALUE_PT,
  MIN_VALUE_INCHES_IN,
  MIN_VALUE_PT,
  PT_PER_INCH,
} from './constants/unit-conversion-limits'
import { formatPlainValueInUnit, formatPointsForDisplay } from './helpers/format-points-for-display'

/**
 * Validate width/height in points against accepted panel bounds. `unit` (the panel's currently
 * displayed unit) determines what the below-minimum/above-maximum messages show the bound as —
 * e.g. "Max-value: 500 pc" in Picas vs. "Max-value: 6000 pt" in Points.
 *
 * `maxPoints` defaults to the pasteboard's `MAX_VALUE_PT` — pass a narrower bound for a
 * photo-filled shape (see `computeMaxPanelFieldValueForPhotoFill` in
 * `object-panel/helpers/photo-fill-resize.ts`) so a value that exceeds *both* bounds still
 * reports the smaller (actually binding) one, not the pasteboard's.
 */
export function validateDimensionPoints(
  points: number,
  unit: UnitOfMeasure = 'Points',
  maxPoints: number = MAX_VALUE_PT
): string | undefined {
  if (!Number.isFinite(points)) {
    return VALIDATION_ERRORS.INVALID_NUMBER
  }

  if (points < 0) {
    return VALIDATION_ERRORS.NEGATIVE_VALUE
  }

  // Inches has its own independent (smaller) floor — see `MIN_VALUE_INCHES_IN`.
  const minPoints = unit === 'Inches' ? MIN_VALUE_INCHES_IN * PT_PER_INCH : MIN_VALUE_PT

  if (points > 0 && points < minPoints) {
    return belowMinimumError(unit)
  }

  if (points === 0) {
    return belowMinimumError(unit)
  }

  if (points > maxPoints) {
    return aboveMaximumError(unit, maxPoints)
  }

  return undefined
}

export function belowMinimumError(unit: UnitOfMeasure): string {
  // Inches is enforced against, and shows, its own independent floor (`MIN_VALUE_INCHES_IN`) —
  // not MIN_VALUE_PT converted to inches (0.01 / 72 ≈ 0.000139 in, both a confusing near-zero
  // value at this precision AND a stricter floor than the natural inches one). Points/Picas share
  // MIN_VALUE_PT and show its real conversion.
  if (unit === 'Inches') {
    return `Min-value: ${MIN_VALUE_INCHES_IN} in`
  }

  return `Min-value: ${formatPlainValueInUnit(MIN_VALUE_PT, unit)}`
}

/**
 * "Max-value: {maxPoints} {unit}". Defaults to the pasteboard's `MAX_VALUE_PT` — pass an
 * explicit `maxPoints` for a narrower bound (e.g. a photo-filled shape's max fill size).
 *
 * Picas show pica-point notation ("500p0") instead of "500 pc" — matching the same XpY
 * notation the W/H fields themselves display and accept as input (`formatPointsForDisplay`).
 */
export function aboveMaximumError(unit: UnitOfMeasure, maxPoints: number = MAX_VALUE_PT): string {
  if (unit === 'Picas') {
    return `Max-value: ${formatPointsForDisplay(maxPoints, 'Picas')}`
  }

  return `Max-value: ${formatPlainValueInUnit(maxPoints, unit)}`
}
